import { BUDGET_OPTION_DEFS, USE_CASE_OPTION_VALUES, VEHICLE_TYPE_OPTION_VALUES } from "./buyerConciergeQuizConstants";
import { formatFreshLabel, formatTransmissionShort } from "./autoclaroHomeUtils";
import { isVerifiedDealer, listingCardMeta } from "./autoclaroSearchUtils";
import { RECOMMENDATION_FAIR_PRICE_BOOST } from "../../constants/automapFairPriceConstants";
import { RISK_LEVELS } from "../../constants/automapVehicleRiskConstants";
import { getFairPriceLabelText, resolveFairPriceLabel } from "../../utils/fairPriceDisplay";
import {
  getRecommendationRiskBoost,
  isUnavailableForRecommendation,
  resolveRiskLevel,
} from "../../utils/vehicleRiskDisplay";
import { BUYER_RECOMMENDATION_MOCK_VEHICLES } from "./data/buyerRecommendationMockData";
import { getVehicleDetailHref } from "../../utils/automapListing/listingDetailRoutes";
import { formatVehicleTitle } from "../../utils/automapListing/listingFormatters";

const VEHICLE_TYPE_BODY_MAP = {
  automovil: ["sedán", "sedan", "hatchback", "coupe"],
  suv: ["suv", "camioneta", "crossover"],
  pickup: ["pickup", "camioneta"],
  familiar: ["suv", "van", "minivan", "wagon"],
  ciudad: ["hatchback", "sedán", "sedan", "compact"],
  trabajo: ["pickup", "van", "camioneta"],
  unsure: [],
};

const USE_CASE_BODY_BOOST = {
  familia: ["suv", "van", "wagon"],
  trabajo: ["pickup", "van"],
  ciudad: ["hatchback", "sedán", "sedan"],
  viajes: ["sedán", "sedan", "suv"],
  montana: ["suv", "pickup", "4x4"],
  primera: ["hatchback", "sedán", "sedan"],
  consumo: ["hatchback", "híbrido", "hybrid"],
  reventa: ["sedán", "sedan", "suv"],
};

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function resolveBudgetBounds(intent) {
  const option = BUDGET_OPTION_DEFS.find((entry) => entry.value === intent.budgetRange);
  const customMax = intent.customMaxBudget ? Number(intent.customMaxBudget) : null;
  return {
    min: option?.priceMin ?? 0,
    max: customMax || option?.priceMax || null,
  };
}

function transmissionMatches(preference, listingTransmission) {
  if (!preference || preference === "cualquiera") return true;
  const normalized = normalizeText(listingTransmission);
  if (preference === "automatico") {
    return normalized.includes("auto");
  }
  if (preference === "mecanico") {
    return normalized.includes("mec") || normalized.includes("manual");
  }
  return true;
}

function fuelMatches(preference, listingFuel) {
  if (!preference || preference === "cualquiera") return true;
  const normalized = normalizeText(listingFuel);
  const map = {
    gasolina: ["gasolina", "gasoline", "gas"],
    diesel: ["diesel", "diésel"],
    hibrido: ["hibrido", "híbrido", "hybrid"],
    electrico: ["electrico", "eléctrico", "electric", "ev"],
  };
  return (map[preference] || []).some((token) => normalized.includes(token));
}

function bodyTypeMatches(vehicleType, bodyType) {
  if (!vehicleType || vehicleType === "unsure") return true;
  const allowed = VEHICLE_TYPE_BODY_MAP[vehicleType] || [];
  if (!allowed.length) return true;
  const normalized = normalizeText(bodyType);
  return allowed.some((token) => normalized.includes(normalizeText(token)));
}

function budgetFitScore(priceCop, bounds) {
  if (priceCop == null) return 0.4;
  const price = Number(priceCop);
  if (!Number.isFinite(price)) return 0.4;
  if (bounds.max != null && price <= bounds.max && price >= bounds.min) return 1;
  if (bounds.max != null && price <= bounds.max * 1.08) return 0.75;
  if (bounds.max != null && price <= bounds.max * 1.15) return 0.5;
  if (bounds.min && price >= bounds.min * 0.85) return 0.35;
  return 0.15;
}

function scoreUseCaseRelevance(useCases, listing) {
  if (!useCases?.length) return 0.5;
  const body = normalizeText(listing.bodyType);
  let score = 0;
  useCases.forEach((useCase) => {
    const boosts = USE_CASE_BODY_BOOST[useCase] || [];
    if (boosts.some((token) => body.includes(normalizeText(token)))) {
      score += 1;
    }
  });
  return Math.min(1, score / Math.max(1, useCases.length));
}

function deriveRiskLevelLabel(listing) {
  const level = resolveRiskLevel(listing);
  const map = {
    [RISK_LEVELS.LOW]: "Bajo",
    [RISK_LEVELS.MEDIUM]: "Medio",
    [RISK_LEVELS.HIGH]: "Alto",
    [RISK_LEVELS.UNKNOWN]: "Medio",
    [RISK_LEVELS.UNAVAILABLE]: "No disponible",
  };
  return map[level] || "Medio";
}

function deriveFairPriceLabel(listing) {
  return getFairPriceLabelText(listing);
}

function hoursSince(isoDate) {
  if (!isoDate) return null;
  return (Date.now() - new Date(isoDate).getTime()) / 3_600_000;
}

function buildMatchReasonEntries(intent, listing, scores) {
  const entries = [];
  const bounds = resolveBudgetBounds(intent);
  const price = listing.priceCop ?? listing.currentPriceCop;

  if (scores.budget >= 0.75) entries.push({ key: "withinBudget" });
  if (scores.transmission >= 1 && intent.transmission && intent.transmission !== "cualquiera") {
    entries.push({
      key: "transmissionMatch",
      transmissionKey: listing.transmission,
    });
  }
  if (scores.useCase >= 0.5 && intent.useCases?.includes("familia")) {
    entries.push({ key: "familyUse" });
  }
  if (scores.useCase >= 0.5 && intent.useCases?.includes("montana")) {
    entries.push({ key: "mountainUse" });
  }
  if (scores.useCase >= 0.5 && intent.useCases?.includes("primera")) {
    entries.push({ key: "firstPurchase" });
  }
  if (listing.dealerVerified || isVerifiedDealer(listing)) {
    entries.push({ key: "verifiedDealer" });
  }
  const fairLabel = deriveFairPriceLabel(listing);
  if (/precio justo|buen precio|fair price|good price|below market/i.test(fairLabel)) {
    entries.push({ key: "fairPrice" });
  }
  if (
    hoursSince(listing.lastConfirmedAt || listing.lastSeenAt) != null &&
    hoursSince(listing.lastConfirmedAt || listing.lastSeenAt) < 24
  ) {
    entries.push({ key: "recentlyConfirmed" });
  }
  if (intent.financingPreference === "financiacion" && listing.financingAvailable) {
    entries.push({ key: "financingAvailable" });
  }
  if (!entries.length && price && bounds.max) {
    entries.push({ key: "profileMatch" });
  }

  const unique = [];
  const seen = new Set();
  entries.forEach((entry) => {
    const id = `${entry.key}:${entry.city || ""}:${entry.transmissionKey || ""}`;
    if (seen.has(id)) return;
    seen.add(id);
    unique.push(entry);
  });
  return unique.slice(0, 4);
}

function normalizeTransmissionKey(value) {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("auto")) return "automatic";
  if (normalized.includes("mec") || normalized.includes("manual")) return "manual";
  if (normalized.includes("cvt")) return "cvt";
  if (normalized.includes("secuencial") || normalized.includes("automated")) return "automated_manual";
  return null;
}

export function formatMatchReasonEntries(entries, t, cardCopy) {
  if (!entries?.length) return [];
  if (typeof t !== "function") {
    return buildMatchReasonsFromEntries(entries);
  }
  return entries.map((entry) => {
    const { key, transmissionKey, ...params } = entry;
    const nextParams = { ...params };
    if (transmissionKey && cardCopy?.listingTransmission) {
      const normalized = normalizeTransmissionKey(transmissionKey) || transmissionKey;
      const label = cardCopy.listingTransmission(normalized);
      nextParams.transmission =
        label && label !== "—" ? label : formatTransmissionShort(transmissionKey);
    }
    const label = t(`automotive.quiz.results.matchReasons.${key}`, nextParams);
    return label !== `automotive.quiz.results.matchReasons.${key}` ? label : key;
  });
}

function buildMatchReasonsFromEntries(entries) {
  const fallback = {
    withinBudget: "Dentro de tu presupuesto",
    nearCity: (entry) => `Disponible cerca de ${entry.city}`,
    transmissionMatch: (entry) =>
      `${formatTransmissionShort(entry.transmissionKey || entry.transmission)}, como prefieres`,
    familyUse: "Buena opción para familia",
    mountainUse: "Adecuada para lomas y montaña",
    firstPurchase: "Opción accesible para primera compra",
    verifiedDealer: "Verificado por AutoMapa",
    fairPrice: "Precio justo frente a similares",
    recentlyConfirmed: "Disponibilidad confirmada recientemente",
    financingAvailable: "Financiación disponible",
    profileMatch: "Se acerca a tu perfil de búsqueda",
  };
  return entries.map((entry) => {
    const value = fallback[entry.key];
    return typeof value === "function" ? value(entry) : value || entry.key;
  });
}

export function scoreListingAgainstIntent(listing, intent) {
  const bounds = resolveBudgetBounds(intent);
  const price = listing.priceCop ?? listing.currentPriceCop;

  const fairLabelKey = resolveFairPriceLabel(listing);
  const fairBoost = RECOMMENDATION_FAIR_PRICE_BOOST[fairLabelKey] ?? 0;
  const riskBoost = getRecommendationRiskBoost(listing);

  const scores = {
    budget: budgetFitScore(price, bounds),
    city: 0.5,
    bodyType: bodyTypeMatches(intent.vehicleType, listing.bodyType) ? 1 : 0.35,
    transmission: transmissionMatches(intent.transmission, listing.transmission) ? 1 : 0.25,
    fuel: fuelMatches(intent.fuel, listing.fuelType || listing.fuel) ? 1 : 0.25,
    useCase: scoreUseCaseRelevance(intent.useCases, listing),
    verified: listing.dealerVerified || isVerifiedDealer(listing) ? 1 : 0.2,
    availability:
      hoursSince(listing.lastConfirmedAt || listing.lastSeenAt || listing.lastObservedAt) != null &&
      hoursSince(listing.lastConfirmedAt || listing.lastSeenAt || listing.lastObservedAt) < 48
        ? 1
        : 0.5,
    fairPrice: /precio justo|buen precio|por debajo|great|good|fair/i.test(deriveFairPriceLabel(listing)) ? 1 : 0.4,
    risk: resolveRiskLevel(listing) === RISK_LEVELS.LOW ? 1 : 0.55,
  };

  const weighted =
    scores.budget * 25 +
    scores.city * 15 +
    scores.bodyType * 15 +
    scores.transmission * 10 +
    scores.fuel * 10 +
    scores.useCase * 10 +
    scores.verified * 5 +
    scores.availability * 5 +
    scores.fairPrice * 5 +
    scores.risk * 5 +
    fairBoost +
    riskBoost;

  const matchScore = Math.round(Math.min(100, Math.max(55, weighted)));
  const matchReasonEntries = buildMatchReasonEntries(intent, listing, scores);
  const matchReasons = buildMatchReasonsFromEntries(matchReasonEntries);

  return { matchScore, matchReasons, matchReasonEntries, scores };
}

export function normalizeListingToRecommendation(listing, intent, slug, locale = null) {
  const { matchScore, matchReasons, matchReasonEntries } = scoreListingAgainstIntent(listing, intent);
  const meta = listingCardMeta(listing);
  const title = formatVehicleTitle(listing);

  return {
    id: String(listing.vehicleListingId || listing.id),
    slug: listing.slug || String(listing.vehicleListingId || listing.id),
    make: listing.make,
    model: listing.model,
    version: listing.trim || listing.version || "",
    year: listing.year,
    priceCop: listing.currentPriceCop ?? listing.priceCop,
    mileageKm: listing.mileageKm,
    city: listing.city,
    neighborhood: listing.neighborhood || null,
    transmission: listing.transmission,
    fuel: listing.fuelType || listing.fuel,
    bodyType: listing.bodyType,
    imageUrl: listing.primaryImageUrl || listing.imageUrl || "/client-views/autoclaro/vehicle-placeholder.jpg",
    dealerName: listing.dealerName || listing.sellerName || listing.sourceName || "Concesionario",
    dealerVerified: listing.dealerVerified ?? meta.verified,
    dealerResponseMinutes: listing.dealerResponseMinutes ?? 45,
    lastConfirmedAt: listing.lastConfirmedAt || listing.lastSeenAt || listing.lastObservedAt || null,
    fairPriceLabel: listing.fairPriceLabel || deriveFairPriceLabel(listing),
    riskLevel: listing.riskLevel ? deriveRiskLevelLabel(listing) : deriveRiskLevelLabel(listing),
    vehicleRiskLevel: resolveRiskLevel(listing),
    financingAvailable: listing.financingAvailable ?? true,
    matchScore,
    matchReasons,
    matchReasonEntries,
    detailPath: listing.vehicleListingId || listing.id
      ? getVehicleDetailHref(listing, slug, { locale })
      : null,
    lastConfirmedLabel: formatFreshLabel(listing),
    title,
  };
}

export function normalizeMockToRecommendation(vehicle, intent, slug) {
  const { matchScore, matchReasons, matchReasonEntries } = scoreListingAgainstIntent(vehicle, intent);
  return {
    ...vehicle,
    matchScore,
    matchReasons,
    matchReasonEntries,
    detailPath: null,
    lastConfirmedLabel: formatFreshLabel({ lastSeenAt: vehicle.lastConfirmedAt }),
    title: [vehicle.year, vehicle.make, vehicle.model, vehicle.version].filter(Boolean).join(" "),
  };
}

export function rankMockRecommendations(intent, slug) {
  return BUYER_RECOMMENDATION_MOCK_VEHICLES.map((vehicle) =>
    normalizeMockToRecommendation(vehicle, intent, slug)
  )
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 4);
}

export function rankInventoryRecommendations(listings, intent, slug, locale = null) {
  return listings
    .filter((listing) => !isUnavailableForRecommendation(listing))
    .map((listing) => normalizeListingToRecommendation(listing, intent, slug, locale))
    .sort((a, b) => b.matchScore - a.matchScore);
}

export function vehicleTypeLabel(value) {
  if (!value) return "vehículo";
  return value;
}

export function timelinePhrase(value) {
  const map = {
    today: "con compra probable esta semana",
    this_month: "con compra probable este mes",
    "1-3_months": "en los próximos 1–3 meses",
    researching: "mientras comparas opciones",
  };
  return map[value] || "";
}

export function buildBuyerNarrativeSummary(intent, summary) {
  if (!intent) return "";

  const type = vehicleTypeLabel(intent.vehicleType);
  const transmission =
    intent.transmission === "automatico"
      ? "automática"
      : intent.transmission === "mecanico"
        ? "mecánica"
        : null;
  const budget = summary?.budget || "tu presupuesto";
  const useCases = (intent.useCases || [])
    .map((value) => value)
    .filter(Boolean)
    .slice(0, 2);

  const parts = [
    `Estás buscando ${type}${transmission ? ` ${transmission}` : ""} en el Valle de Aburrá y cercanías, ${budget.toLowerCase()}`,
  ];
  if (useCases.length) {
    parts.push(`ideal para ${useCases.join(" y ")}`);
  }
  const timeline = timelinePhrase(intent.purchaseTimeline);
  if (timeline) parts.push(timeline);

  return `${parts.join(", ")}.`;
}
